import type { ClienteApi } from '../cliente';
import { claveIdempotencia, consulta, negocio, type Pagina } from '../comun';
import type { components, operations } from '../tipos/analitica';

/**
 * Operaciones de «Indicadores» (7.2 Indicadores y 7.3 Exportaciones) con su `operationId` y los tipos generados de
 * `analitica.yaml`. Cada indicador pide su permiso de lectura (`read_permission`); exportar pide
 * `analitica.indicadores.exportar` (también en solo lectura). Los códigos de los indicadores salen siempre de
 * `listIndicators`: el panel no tiene ninguno escrito.
 *
 * - `createAnalyticsExport` lleva `Idempotency-Key` (obligatoria).
 * - `queryIndicator` también la define `ops/analitica.ts` (Inicio, 7.1) con la misma forma.
 */
type Esquema<N extends keyof components['schemas']> = components['schemas'][N];

export type Indicador = Esquema<'IndicatorSummary'>;
export type DefinicionIndicador = Esquema<'IndicatorDefinition'>;
export type ListaIndicadores = Esquema<'IndicatorList'>;
export type AreaIndicador = Esquema<'IndicatorArea'>;
export type UnidadIndicador = Esquema<'IndicatorUnit'>;
export type Dimension = Esquema<'Dimension'>;
export type CifrasIndicador = Esquema<'IndicatorValues'>;
export type SerieIndicador = Esquema<'Series'>;
export type PuntoIndicador = Esquema<'DataPoint'>;
export type Comparacion = Esquema<'Comparison'>;
export type ModoComparacion = Esquema<'CompareMode'>;
export type Agrupacion = Esquema<'Granularity'>;
export type MotivoIncompleto = Esquema<'IncompleteReason'>;
export type Exportacion = Esquema<'AnalyticsExport'>;
export type ListaExportaciones = Esquema<'AnalyticsExportList'>;
export type NuevaExportacion = Esquema<'AnalyticsExportCreate'>;
export type EstadoExportacion = Esquema<'ExportStatus'>;
export type ConsultaCifras = NonNullable<operations['queryIndicator']['parameters']['query']>;

const r = (businessId: string, resto: string): `/v1/${string}` => `${negocio(businessId)}/analytics${resto}`;
const id = encodeURIComponent;

export function operacionesIndicadores(api: ClienteApi) {
    return {
        /** listIndicators: el catálogo que la persona puede leer (permiso de cada indicador y bloques activos), por área. */
        listIndicators: (businessId: string, filtros: { area?: AreaIndicador } & Pagina = {}) =>
            api.pedir<ListaIndicadores>('GET', r(businessId, `/indicators${consulta({ ...filtros })}`)),
        /** getIndicator: la definición (cómo se calcula, eventos, versión vigente). */
        getIndicator: (businessId: string, codigo: string) => api.pedir<DefinicionIndicador>('GET', r(businessId, `/indicators/${id(codigo)}`)),
        /**
         * queryIndicator: la serie (hasta 25 meses), con `granularity`, `location_id`, `breakdown` por una dimensión del
         * indicador y `compare` (`none` por defecto). «Lo suyo» lo fuerza la API (`own_scope_applied`).
         */
        queryIndicator: (businessId: string, codigo: string, filtros: ConsultaCifras) =>
            api.pedir<CifrasIndicador>(
                'GET',
                r(businessId, `/indicators/${id(codigo)}/values${consulta({ ...filtros, dimension_values: filtros.dimension_values?.join(',') })}`),
            ),
        /** listAnalyticsExports (`analitica.indicadores.exportar`): solo las que ha pedido quien mira. */
        listAnalyticsExports: (businessId: string, filtros: { status?: EstadoExportacion } & Pagina = {}) =>
            api.pedir<ListaExportaciones>('GET', r(businessId, `/exports${consulta({ ...filtros })}`)),
        /** createAnalyticsExport: 202 en preparación (o ya lista); hasta 20 indicadores y 25 meses. */
        createAnalyticsExport: (businessId: string, datos: NuevaExportacion, clave = claveIdempotencia()) =>
            api.pedir<Exportacion>('POST', r(businessId, '/exports'), { cuerpo: datos, idempotencia: clave }),
        /** getAnalyticsExport: con `ready`, el enlace firmado (`download_url`, 7 días). */
        getAnalyticsExport: (businessId: string, exportacionId: string) => api.pedir<Exportacion>('GET', r(businessId, `/exports/${id(exportacionId)}`)),
    };
}
